import moment from 'moment';
import { ClipDeletes, CompositeSegment } from '../types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { VideoSegment } from '../types/RemotionPlayerConfigTypes/Segment/VideoSegment';
import { roundToNDecimalPlaces } from './mathUtils';
import { Clip } from '../types/clip-types';
import { SegmentType } from '../types/Segment/Type';
import { isSplitsInTimelineEnabledPure, getClipFPS, getAllCompositeSegments, getAllSegments } from './clipUtils';
import { approxGTE, approxLT, approxLTE } from './compareUtils';
import { getSegmentsTrack } from './playerConfigGetters';
import { Segment } from '../types/RemotionPlayerConfigTypes/Segment/Segment';

export function timeStringToSeconds(timeString): number {
  const components: number[] = timeString.split(':').map(parseFloat);
  const [hours, minutes, seconds] = components;
  return hours * 3600 + minutes * 60 + seconds;
}
const TIME_STRING_FORMAT = 'HH:mm:ss.SSS';
export const TIME_INPUT_MASK = '**:99:69.999';

export function convertSecondsToTimeString(numberInSeconds: number, format = TIME_STRING_FORMAT) {
  return moment.utc(numberInSeconds * 1000).format(format);
}
export const isFallingBetweenRange = (
  ranges: { start: number; end: number }[],
  k: number,
  includeBoundaries = false
) => {
  return ranges.some(({ start, end }) => {
    if (includeBoundaries) {
      return start <= k && k <= end;
    }
    return start < k && k < end;
  });
}; // !!TODO @HiteshChoudhary0098 need to sort segments in a more efficient way need to store the values for sorted deletes
/**
 * Time Nomenclature Documentation
 *
 * This file uses specific terminology for different time representations:
 *
 * - **absoluteTime**: Time in the absolute timeline - represents the time as seen in the timeline this includes all the deleted in the composite segment
 *
 * - **playerTime**: This is the time as seen in the actual player this is the time that is used to play the clip
 *
 * - **segmentTime**: Time within a specific segment - includes the effects of deletes and trim operations applied to that particular segment
 *
 */
/**
 * Calculates the total deleted time from the beginning of the segment up to a specific time point.
 * This function accounts for deleted segments within the segment's bounds.
 *
 * @param playerTime
 * @param deletes - Object containing all deleted time ranges in the clip
 * @param segment - The segment containing the time bounds
 * @param fps - Frames per second, defaults to FPS_24
 * @returns The total duration of deleted segments up to the specified player time
 */

export const getSegmentDeleteTimeTillPlayerTime = (
  playerTime: number,
  deletes: ClipDeletes,
  segment: CompositeSegment | VideoSegment,
  fps: number
) => {
  const startTrimOffset = (segment.currentBounds?.fromFrame || 0) / fps;
  const endTrimOffset = (segment.currentBounds?.toFrame || 0) / fps;
  const segmentStartTime = (segment?.fromFrame || 0) / fps;

  const deleteTime = Object.entries(deletes)
    .sort((itemA, itemB) => itemA[1].bounds[0] - itemB[1].bounds[0])
    .reduce((acc, [_, range]) => {
      if (
        range.bounds[0] !== 0 &&
        roundToNDecimalPlaces(range.bounds[0], 3) !== roundToNDecimalPlaces(endTrimOffset, 3) &&
        range.bounds[0] < endTrimOffset &&
        range.bounds[0] <= playerTime - segmentStartTime + acc + startTrimOffset
      ) {
        // A delete can start before the trim window — clamp to startTrimOffset so we don't count deleted time outside the visible segment.
        // e.g. delete [10, 25], startTrimOffset=17 → effectiveStart=17, not 10 (the [10,17] part is already trimmed)
        const effectiveStart = Math.max(range.bounds[0], startTrimOffset);
        // A delete can extend past the trim end — clamp to endTrimOffset so we don't count deleted time that is already trimmed away.
        // e.g. delete [17.8, 119], endTrimOffset=21 → effectiveEnd=21, not 119 (the [21,119] part is already trimmed)
        const effectiveEnd = Math.min(range.bounds[1], endTrimOffset);
        return acc + Math.max(0, effectiveEnd - effectiveStart);
      }
      return acc;
    }, 0);
  return deleteTime;
};

export const getAbsolutePlayerTimePure = ({
  time,
  clipData,
  minSegmentWidth,
  zoomLevel,
  isSplitsTranslationEnabled
}: {
  time: number;
  clipData: Clip;
  minSegmentWidth: number;
  zoomLevel: number;
  isSplitsTranslationEnabled: boolean;
}) => {
  const playerConfig = clipData.asset_metadata.player_config;
  const isSplitsEnabled = isSplitsInTimelineEnabledPure({ clip: clipData, isSplitsTranslationEnabled });

  if (!playerConfig) {
    return 0;
  }
  const fps = getClipFPS(clipData);

  const segmentsTrack = getSegmentsTrack(playerConfig);
  const sortedSegments = [...segmentsTrack.segments].sort((a, b) => a.fromFrame - b.fromFrame);
  const totalAdditionalTime = sortedSegments.reduce((additionalTime, segment) => {
    const currentSegmentStartTime = segment.fromFrame / fps;
    if (approxGTE(currentSegmentStartTime, time)) {
      return additionalTime;
    }

    if (segment.type === SegmentType.COMPOSITE) {
      //  Not supporting min width for composite segments yet
      return (
        additionalTime +
        getSegmentDeleteTimeTillPlayerTime(
          time,
          getDeletesForSegmentPure({ clip: clipData, segmentId: segment.id, isSplitsTranslationEnabled }),
          segment,
          fps
        )
      );
    }

    if (segment.type === SegmentType.VIDEO && isSplitsEnabled) {
      const totalSegmentTimeLength =
        ((segment.currentBounds?.toFrame || 0) - (segment.currentBounds?.fromFrame || 0)) * zoomLevel;
      const totalSegmentTime = (segment.toFrame - segment.fromFrame) / fps;

      // When the segment is larger than the min segment width, we can just return the deleted time
      if (approxGTE(totalSegmentTimeLength, minSegmentWidth)) {
        return (
          additionalTime +
          getSegmentDeleteTimeTillPlayerTime(time, segment.segment_metadata?.deletes || {}, segment, fps)
        );
      }

      // When the segment is smaller than the min segment but the time exceeds this segment
      if (approxLTE(totalSegmentTime, time - currentSegmentStartTime)) {
        return additionalTime + (minSegmentWidth / zoomLevel / fps - totalSegmentTime);
      }

      // When the segment is smaller than the min segment but the time does not exceed this segment we need to adjust for scaling and for deletes
      return (
        additionalTime +
        (time - currentSegmentStartTime) * (minSegmentWidth / totalSegmentTimeLength - 1) +
        getSegmentDeleteTimeTillPlayerTime(time, segment.segment_metadata?.deletes || {}, segment, fps) *
          (minSegmentWidth / totalSegmentTimeLength)
      );
    }

    const totalSegmentTime = (segment.toFrame - segment.fromFrame) / fps;
    const isSegmentSizeSmallerThanMinSegmentWidth = approxLTE(totalSegmentTime * fps * zoomLevel, minSegmentWidth);

    if (isSegmentSizeSmallerThanMinSegmentWidth) {
      if (approxLTE(totalSegmentTime, time - currentSegmentStartTime)) {
        return additionalTime + minSegmentWidth / (fps * zoomLevel) - totalSegmentTime;
      }

      return (
        additionalTime + (minSegmentWidth / (fps * zoomLevel) / totalSegmentTime - 1) * (time - currentSegmentStartTime)
      );
    }

    return additionalTime;
  }, 0);

  return time + totalAdditionalTime;
};
export const getDeletesForSegmentPure = ({
  clip,
  segmentId,
  isSplitsTranslationEnabled
}: {
  clip: Clip;
  segmentId?: string;
  isSplitsTranslationEnabled: boolean;
}) => {
  const splitsEnabled = isSplitsInTimelineEnabledPure({
    clip,
    isSplitsTranslationEnabled
  });
  const hasMultipleSegments = getAllCompositeSegments(clip).length > 1;
  if (!splitsEnabled || !clip.asset_metadata.player_config) {
    return clip.asset_metadata.deletes || {};
  }
  if (!segmentId && hasMultipleSegments) {
    throw new Error('Multiple segments found, please provide a segmentId');
  }
  if (segmentId) {
    const segment = getAllSegments(clip).find(segment => segment.id === segmentId);
    if (!segment) {
      throw new Error('Segment not found');
    }
    if (segment.type !== SegmentType.COMPOSITE && segment.type !== SegmentType.VIDEO) {
      return {};
    }
    return segment.segment_metadata?.deletes || {};
  }
  const firstSegment = getAllCompositeSegments(clip)[0];
  return firstSegment.segment_metadata?.deletes || {};
}; /**
 * Converts absolute time to player time for that segment.
 * This function handles the conversion between absolute time (including deleted segments) and the actual time within the composite segment.
 *
 * @param remainingAbsoluteTime - The time after removing any previous elements absolute times and includes deleted segments (time with added delete time but not the trim timing)
 * @param deletes - Object containing all deleted time ranges in the clip
 * @param compositeSegment - The composite segment containing the time bounds
 * @returns An object containing:
 *   - segmentTime: The time within the composite segment after accounting for deleted segments
 *   - timeExceeded: The time exceeded beyond the composite segment bounds
 */
export const getSegmentPlayerTimeFromRemainingAbsoluteTimeForVideoAndCompositeSegments = (
  remainingAbsoluteTime: number,
  deletes: ClipDeletes,
  segment: CompositeSegment | VideoSegment,
  fps: number
) => {
  const startOffset = (segment.currentBounds?.fromFrame || 0) / fps;
  const maxBound = (segment.currentBounds?.toFrame || 0) / fps;
  const sortedDeletes = Object.entries(deletes)
    .sort((a, b) => a[1].bounds[0] - b[1].bounds[0])
    // Include deletes that start before maxBound — those that extend past it will be clamped below.
    // Previously this filtered on bounds[1] <= maxBound, which dropped deletes that partially overlap the trim window.
    .filter(([_, range]) => approxLT(range.bounds[0], maxBound));
  const segmentTime = remainingAbsoluteTime + startOffset;
  let deletedTime = 0;
  for (const [_, range] of sortedDeletes) {
    // Clamp the delete end to maxBound so we don't count deleted time outside the trim window.
    // e.g. delete [17.8, 119], maxBound=21 → clampedEnd=21, not 119
    //
    // NOTE: range.bounds[0] is intentionally NOT clamped to startOffset here.
    // The pre-trim delete [0, startOffset] must be counted from 0 so it cancels out
    // the `+ startOffset` in `segmentTime = remainingAbsoluteTime + startOffset`.
    // Clamping to startOffset would make it contribute 0, leaving startOffset un-subtracted.
    const clampedEnd = Math.min(range.bounds[1], maxBound);
    if (approxLTE(clampedEnd, segmentTime)) {
      deletedTime += clampedEnd - range.bounds[0];
    } else if (approxLTE(range.bounds[0], segmentTime)) {
      deletedTime += segmentTime - range.bounds[0];
    }
  }
  const segmentTimeWithoutTrimAndDeletedTime = Math.min(
    Math.max(0, segmentTime - deletedTime),
    (segment.toFrame - segment.fromFrame) / fps
  );

  return {
    segmentTime: segmentTimeWithoutTrimAndDeletedTime,
    timeExceeded: segmentTime - segmentTimeWithoutTrimAndDeletedTime - deletedTime
  };
}; /**
 * Converts absolute player time to segment time by removing the deleted time.
 * This function handles both composite and non-composite segments.
 * This function is only be called with remaining absolute time that is greater than 0.
 *
 * @param remainingAbsoluteTime - The remaining absolute time that includes deleted segments
 * @param segment - The segment to calculate time for (can be any segment type)
 * @param clipData - The clip data containing metadata and deletes information
 * @returns An object containing:
 *   - segmentTime: The time within the segment after accounting for deleted segments
 *   - timeExceeded: The time exceeded beyond the segment bounds
 */

export const getSegmentPlayerTimeFromRemainingAbsoluteTime = ({
  clipData,
  segment,
  remainingAbsoluteTime,
  minSegmentWidth,
  isSplitsTranslationEnabled,
  zoomLevel
}: {
  remainingAbsoluteTime: number;
  segment: Segment;
  clipData: Clip;
  minSegmentWidth: number;
  isSplitsTranslationEnabled: boolean;
  zoomLevel: number;
}) => {
  const fps = getClipFPS(clipData);
  if (segment.type === SegmentType.COMPOSITE) {
    const deletes = getDeletesForSegmentPure({ clip: clipData, segmentId: segment.id, isSplitsTranslationEnabled });
    return getSegmentPlayerTimeFromRemainingAbsoluteTimeForVideoAndCompositeSegments(
      remainingAbsoluteTime,
      deletes,
      segment,
      fps
    );
  }
  const isSplitsEnabled = isSplitsInTimelineEnabledPure({ clip: clipData, isSplitsTranslationEnabled });
  if (isSplitsEnabled && segment.type === SegmentType.VIDEO) {
    const segmentTrimStartTime = segment.currentBounds?.fromFrame || 0;
    const segmentTrimEndTime = segment.currentBounds?.toFrame || 0;
    const segmentTotalLength = (segmentTrimEndTime - segmentTrimStartTime) * zoomLevel;
    // When the segment is less than the min segment width, we need to adjust for scaling
    if (approxLTE(segmentTotalLength, minSegmentWidth)) {
      const totalSegmentTime = (segment.toFrame - segment.fromFrame) / fps;
      const absoluteTimeWidthOfSegment = minSegmentWidth / zoomLevel / fps;
      // When the absolute time is greater than the absolute time width of the segment, we need to adjust for scaling
      if (approxGTE(remainingAbsoluteTime, absoluteTimeWidthOfSegment)) {
        return {
          segmentTime: totalSegmentTime,
          timeExceeded: remainingAbsoluteTime - absoluteTimeWidthOfSegment
        };
      }
      // When the absolute time is smaller than the absolute time width of the segment, we need to adjust for scaling and for deletes
      const absoluteTimeAdjustedForMinSegmentWidth = remainingAbsoluteTime / (minSegmentWidth / segmentTotalLength);
      return getSegmentPlayerTimeFromRemainingAbsoluteTimeForVideoAndCompositeSegments(
        absoluteTimeAdjustedForMinSegmentWidth,
        segment.segment_metadata?.deletes || {},
        segment,
        fps
      );
    }
    return getSegmentPlayerTimeFromRemainingAbsoluteTimeForVideoAndCompositeSegments(
      remainingAbsoluteTime,
      segment.segment_metadata?.deletes || {},
      segment,
      fps
    );
  }
  const segmentTotalLength = (segment.toFrame - segment.fromFrame) * zoomLevel;
  if (approxLTE(segmentTotalLength, minSegmentWidth)) {
    const totalSegmentTime = (segment.toFrame - segment.fromFrame) / fps;
    const absoluteTimeWidthOfSegment = minSegmentWidth / zoomLevel / fps;
    if (approxGTE(remainingAbsoluteTime, absoluteTimeWidthOfSegment)) {
      return {
        segmentTime: totalSegmentTime,
        timeExceeded: remainingAbsoluteTime - absoluteTimeWidthOfSegment
      };
    }
    const segmentTime = (remainingAbsoluteTime * totalSegmentTime) / absoluteTimeWidthOfSegment;
    return {
      segmentTime,
      timeExceeded: 0
    };
  }

  const segmentTime = Math.min((segment.toFrame - segment.fromFrame) / fps, remainingAbsoluteTime);

  return {
    segmentTime,
    timeExceeded: remainingAbsoluteTime - segmentTime
  };
};
export const getPlayerTimeFromAbsoluteTimePure = ({
  absoluteTime,
  clipData,
  minSegmentWidth,
  isSplitsTranslationEnabled,
  zoomLevel
}: {
  absoluteTime: number;
  clipData: Clip;
  minSegmentWidth: number;
  isSplitsTranslationEnabled: boolean;
  zoomLevel: number;
}) => {
  const playerConfig = clipData.asset_metadata.player_config;

  if (!playerConfig) {
    return 0;
  }

  const segmentsTrack = getSegmentsTrack(playerConfig);
  if (!segmentsTrack || !segmentsTrack.segments.length) {
    return absoluteTime;
  }

  let playerTime = 0;
  let remainingAbsoluteTime = absoluteTime;
  const sortedSegments = [...segmentsTrack.segments].sort((a, b) => a.fromFrame - b.fromFrame);
  for (const segment of sortedSegments) {
    if (approxLTE(remainingAbsoluteTime, 0)) {
      break;
    }

    const { segmentTime, timeExceeded } = getSegmentPlayerTimeFromRemainingAbsoluteTime({
      remainingAbsoluteTime,
      segment,
      clipData,
      minSegmentWidth,
      isSplitsTranslationEnabled,
      zoomLevel
    });

    playerTime += segmentTime;
    remainingAbsoluteTime = timeExceeded;
  }

  return playerTime;
};
